import { useRef, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useCatalog } from '../../context/CatalogContext'
import { deleteMenuImage, uploadMenuImage } from '../../services/menuImages'
import { deleteCampaign, saveCampaign } from '../../services/products'
import type { Campaign, CampaignKind } from '../../types'
import { describeRule, describeWhen } from '../../utils/campaigns'
import { stockCampaignImage } from '../../utils/campaignImage'
import { liraToKurus } from '../../utils/money'
import { CheckIcon, PencilIcon, PlusIcon, TrashIcon, XIcon } from '../icons'
import { DateField, TimeField } from '../PickerFields'
import './KitchenScreensPanel.css'
import './CampaignsPanel.css'

/**
 * Ayarlar > Kampanyalar (14 Eki 2026).
 *
 * Patron kuralı kuruyor; kasada adisyon kurala uyunca öneri olarak çıkıyor,
 * kasiyer eklerse ödenecekten düşüyor. Hesap utils/campaigns.ts'te.
 */

interface Form {
  id?: string
  name: string
  kind: CampaignKind
  percent: string
  amount: string
  minTotal: string
  buyQty: string
  payQty: string
  categoryIds: string[]
  productIds: string[]
  days: number[]
  startTime: string
  endTime: string
  startsOn: string
  endsOn: string
  active: boolean
  sortOrder: number
  showOnQr: boolean
  imageUrl: string | null
  /** Kayıtlı afiş: pencereden vazgeçilince yeni yüklenen silinsin diye. */
  savedImageUrl: string | null
}

const KINDS: { kind: CampaignKind; label: string; hint: string }[] = [
  { kind: 'percent', label: 'Yüzde indirim', hint: 'Seçilen ürünlerde %' },
  { kind: 'amount', label: 'Tutar indirimi', hint: 'Belli tutarın üstüne' },
  { kind: 'bogo', label: 'X al Y öde', hint: 'En ucuzu bedava' },
]

// Pazartesiden başlayarak; değerler Date.getDay() (0 Pazar).
const WEEK: { day: number; label: string }[] = [
  { day: 1, label: 'Pzt' },
  { day: 2, label: 'Sal' },
  { day: 3, label: 'Çar' },
  { day: 4, label: 'Per' },
  { day: 5, label: 'Cum' },
  { day: 6, label: 'Cmt' },
  { day: 0, label: 'Paz' },
]

const lira = (kurus?: number) => (kurus ? String(kurus / 100).replace('.', ',') : '')

const toForm = (c: Campaign): Form => ({
  id: c.id,
  name: c.name,
  kind: c.kind,
  percent: c.percent ? String(c.percent) : '',
  amount: lira(c.amountKurus),
  minTotal: lira(c.minTotalKurus),
  buyQty: c.buyQty ? String(c.buyQty) : '3',
  payQty: c.payQty ? String(c.payQty) : '2',
  categoryIds: c.categoryIds,
  productIds: c.productIds,
  days: c.days,
  startTime: c.startTime ?? '',
  endTime: c.endTime ?? '',
  startsOn: c.startsOn ?? '',
  endsOn: c.endsOn ?? '',
  active: c.active,
  sortOrder: c.sortOrder,
  showOnQr: c.showOnQr,
  imageUrl: c.imageUrl ?? null,
  savedImageUrl: c.imageUrl ?? null,
})

const blank = (sortOrder: number): Form => ({
  name: '',
  kind: 'percent',
  percent: '10',
  amount: '',
  minTotal: '',
  buyQty: '3',
  payQty: '2',
  categoryIds: [],
  productIds: [],
  days: [],
  startTime: '',
  endTime: '',
  startsOn: '',
  endsOn: '',
  active: true,
  sortOrder,
  // Yeni kampanya QR menüde de görünsün; istemeyen kapatır.
  showOnQr: true,
  imageUrl: null,
  savedImageUrl: null,
})

/** Formdan kural; eksik ya da tutarsızsa hata metni. */
function fromForm(f: Form): { campaign?: Omit<Campaign, 'id'> & { id?: string }; error?: string } {
  const name = f.name.trim()
  if (!name) return { error: 'Kampanyaya bir ad verin.' }
  const minTotal = f.minTotal.trim() ? liraToKurus(f.minTotal) : undefined
  if (minTotal === null) return { error: 'En az tutarı rakamla yazın.' }
  const base = {
    id: f.id,
    name,
    kind: f.kind,
    categoryIds: f.categoryIds,
    productIds: f.productIds,
    days: f.days.length === 7 ? [] : f.days,
    startTime: f.startTime && f.endTime ? f.startTime : undefined,
    endTime: f.startTime && f.endTime ? f.endTime : undefined,
    startsOn: f.startsOn || undefined,
    endsOn: f.endsOn || undefined,
    active: f.active,
    sortOrder: f.sortOrder,
    showOnQr: f.showOnQr,
    imageUrl: f.imageUrl,
  }
  if ((f.startTime && !f.endTime) || (!f.startTime && f.endTime)) {
    return { error: 'Saat aralığı için başlangıç ve bitişi birlikte girin.' }
  }
  if (f.startsOn && f.endsOn && f.endsOn < f.startsOn) {
    return { error: 'Bitiş tarihi başlangıçtan önce olamaz.' }
  }
  if (f.kind === 'percent') {
    const p = Number(f.percent.replace(',', '.'))
    if (!(p > 0 && p <= 100)) return { error: 'Yüzdeyi 1 ile 100 arasında yazın.' }
    return { campaign: { ...base, percent: p, minTotalKurus: minTotal } }
  }
  if (f.kind === 'amount') {
    const a = liraToKurus(f.amount)
    if (!a || a <= 0) return { error: 'İndirim tutarını yazın.' }
    return { campaign: { ...base, amountKurus: a, minTotalKurus: minTotal } }
  }
  const buy = Number(f.buyQty)
  const pay = Number(f.payQty)
  if (!(Number.isInteger(buy) && Number.isInteger(pay) && buy >= 2 && pay >= 1 && pay < buy)) {
    return { error: '"Kaç al, kaç öde" için örneğin 3 al 2 öde yazın.' }
  }
  return { campaign: { ...base, buyQty: buy, payQty: pay } }
}

export function CampaignsPanel({ tenantId }: { tenantId: string }) {
  const { campaigns, activeCategories, activeProducts, categories, products } = useCatalog()
  const { hasFeature } = useAuth()
  /** Afiş yükleme Görselli menü modülüyle; kapalıysa stok fotoğraf. */
  const photoOn = hasFeature('gorsel')
  const fileInput = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [form, setForm] = useState<Form | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<Campaign | null>(null)
  /** Ürün seçimi açık mı (kategori yerine tek tek ürün). */
  const [pickProducts, setPickProducts] = useState(false)

  const scopeOf = (c: { categoryIds: string[]; productIds: string[] }) => {
    const names = [
      ...c.categoryIds.map((id) => categories.find((x) => x.id === id)?.name).filter(Boolean),
      ...c.productIds.map((id) => products.find((x) => x.id === id)?.name).filter(Boolean),
    ] as string[]
    if (names.length === 0) return 'Bütün menü'
    return names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', ')
  }

  /** Afişi olmayan kampanyada QR menünün koyacağı stok fotoğraf. */
  const stockOf = (f: Form) =>
    stockCampaignImage([
      ...f.categoryIds.map((id) => categories.find((x) => x.id === id)?.name ?? ''),
      ...f.productIds.map((id) => products.find((x) => x.id === id)?.name ?? ''),
      ...products.filter((p) => f.categoryIds.includes(p.categoryId)).map((p) => p.name),
    ])

  /** Seçilen afişi küçültüp hemen yükler; kampanyaya Kaydet'te bağlanır. */
  const onPickImage = async (file: File | undefined) => {
    if (!file || !form) return
    setUploading(true)
    setError(null)
    try {
      const url = await uploadMenuImage(tenantId, `kampanya-${form.id ?? 'yeni'}`, file)
      if (form.imageUrl && form.imageUrl !== form.savedImageUrl) void deleteMenuImage(form.imageUrl)
      setForm((f) => (f ? { ...f, imageUrl: url } : f))
    } catch (err) {
      console.error('[campaign] image', err)
      setError(err instanceof Error && err.message.includes('açılamadı') ? err.message : 'Afiş yüklenemedi. İnternet bağlantınızı kontrol edin.')
    } finally {
      setUploading(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  /** Pencere kapanırken: kaydedilmeyen yeni afişi sil. */
  const close = () => {
    if (form?.imageUrl && form.imageUrl !== form.savedImageUrl) void deleteMenuImage(form.imageUrl)
    setForm(null)
  }

  const open = (f: Form) => {
    setError(null)
    setPickProducts(f.productIds.length > 0)
    setForm(f)
  }

  const onSave = async () => {
    if (!form) return
    const { campaign, error: problem } = fromForm(form)
    if (!campaign) {
      setError(problem ?? 'Eksik bilgi.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await saveCampaign(tenantId, campaign)
      if (form.savedImageUrl && form.savedImageUrl !== form.imageUrl) void deleteMenuImage(form.savedImageUrl)
      setForm(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kaydedilemedi.')
    } finally {
      setBusy(false)
    }
  }

  const toggleActive = async (c: Campaign) => {
    try {
      await saveCampaign(tenantId, { ...c, active: !c.active })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kaydedilemedi.')
    }
  }

  const onRemove = async () => {
    if (!confirmRemove) return
    setBusy(true)
    try {
      await deleteCampaign(tenantId, confirmRemove.id)
      void deleteMenuImage(confirmRemove.imageUrl)
      setConfirmRemove(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Silinemedi.')
    } finally {
      setBusy(false)
    }
  }

  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v])

  return (
    <div className="kscr camp-set">
      <p className="kscr__lead">
        Kurduğunuz kampanya, adisyon kurala uyunca kasada "Kampanya var" diye çıkar. Kasiyer eklerse
        tutar ödenecekten düşer, fişte ve raporda ayrı satır olarak görünür. İsterseniz QR menüyü açan
        müşteriye de görselli bir pencereyle gösterilir.
      </p>

      {error && !form && <p className="kscr__error">{error}</p>}

      {campaigns.length === 0 ? (
        <p className="kscr__empty">Henüz kampanya yok.</p>
      ) : (
        <ul className="kscr__list">
          {campaigns.map((c) => {
            const when = describeWhen(c)
            return (
              <li key={c.id} className={c.active ? '' : 'is-off'}>
                <div className="kscr__info">
                  <b>{c.name}</b>
                  <span>{describeRule(c, scopeOf(c))}</span>
                  {when && <span>{when}</span>}
                  {c.showOnQr && <span className="camp-set__qr">QR menüde görünüyor</span>}
                </div>
                <button
                  type="button"
                  className={`camp-set__switch ${c.active ? 'is-on' : ''}`}
                  role="switch"
                  aria-checked={c.active}
                  aria-label={`${c.name} ${c.active ? 'açık' : 'kapalı'}`}
                  onClick={() => void toggleActive(c)}
                >
                  <span />
                </button>
                <div className="kscr__actions">
                  <button
                    type="button"
                    className="icon-btn"
                    title="Düzenle"
                    aria-label={`${c.name} kampanyasını düzenle`}
                    onClick={() => open(toForm(c))}
                  >
                    <PencilIcon />
                  </button>
                  <button
                    type="button"
                    className="icon-btn is-danger"
                    title="Sil"
                    aria-label={`${c.name} kampanyasını sil`}
                    onClick={() => setConfirmRemove(c)}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <button
        type="button"
        className="btn btn--primary kscr__add"
        onClick={() => open(blank(campaigns.length))}
      >
        <PlusIcon />
        Kampanya ekle
      </button>

      {form && (
        <div className="cdlg" role="dialog" aria-modal="true" aria-label={form.id ? 'Kampanyayı düzenle' : 'Kampanya ekle'}>
          {/* İki sütun, ürün penceresi gibi (kullanıcı, 14 Eki 2026): solda ad,
              tür ve tutar; sağda ne zaman ve hangi ürünlerde. Pencere ekrana
              sığıyor, yalnız uzun ürün listesi kendi içinde kayıyor. */}
          <form
            className="cdlg__card"
            onSubmit={(e) => {
              e.preventDefault()
              void onSave()
            }}
          >
            <header className="cdlg__head">
              <h2>{form.id ? 'Kampanyayı düzenle' : 'Kampanya ekle'}</h2>
              <button type="button" className="cdlg__x" onClick={close} aria-label="Kapat">
                <XIcon />
              </button>
            </header>

            <div className="cdlg__cols">
              <aside className="cdlg__side">
                <label className="cdlg__field">
                  <span>Kampanyanın adı</span>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Happy Hour"
                    maxLength={40}
                    required
                  />
                  <small>Kasada ve fişte bu ad görünür.</small>
                </label>

                {/* Tür radyo düğmesiyle seçiliyor; seçilen türün ayarları hemen
                    altında açılıyor (kullanıcı, 14 Eki 2026). */}
                <fieldset className="cdlg__kinds">
                  <legend>Türü</legend>
                  {KINDS.map((k) => {
                    const on = form.kind === k.kind
                    return (
                      <div key={k.kind} className={on ? 'cdlg__kind is-on' : 'cdlg__kind'}>
                        <label className="cdlg__radio">
                          <input
                            type="radio"
                            name="campaign-kind"
                            value={k.kind}
                            checked={on}
                            onChange={() => setForm({ ...form, kind: k.kind })}
                          />
                          <span className="cdlg__dot" aria-hidden="true"></span>
                          <span className="cdlg__radio-text">
                            <b>{k.label}</b>
                            <small>{k.hint}</small>
                          </span>
                        </label>
                        {on && (
                          <div className="cdlg__pair">
                            {k.kind === 'percent' && (
                              <>
                          <label className="cdlg__field">
                            <span>İndirim (%)</span>
                            <input
                              inputMode="decimal"
                              value={form.percent}
                              onChange={(e) => setForm({ ...form, percent: e.target.value.replace(/[^\d,]/g, '') })}
                            />
                          </label>
                          <label className="cdlg__field">
                            <span>En az (₺)</span>
                            <input
                              inputMode="decimal"
                              value={form.minTotal}
                              onChange={(e) => setForm({ ...form, minTotal: e.target.value.replace(/[^\d,]/g, '') })}
                              placeholder="Yok"
                            />
                          </label>
                              </>
                            )}
                            {k.kind === 'amount' && (
                              <>
                          <label className="cdlg__field">
                            <span>İndirim (₺)</span>
                            <input
                              inputMode="decimal"
                              value={form.amount}
                              onChange={(e) => setForm({ ...form, amount: e.target.value.replace(/[^\d,]/g, '') })}
                              placeholder="50"
                            />
                          </label>
                          <label className="cdlg__field">
                            <span>Adisyon en az (₺)</span>
                            <input
                              inputMode="decimal"
                              value={form.minTotal}
                              onChange={(e) => setForm({ ...form, minTotal: e.target.value.replace(/[^\d,]/g, '') })}
                              placeholder="500"
                            />
                          </label>
                              </>
                            )}
                            {k.kind === 'bogo' && (
                              <>
                          <label className="cdlg__field">
                            <span>Kaç al</span>
                            <input
                              inputMode="numeric"
                              value={form.buyQty}
                              onChange={(e) => setForm({ ...form, buyQty: e.target.value.replace(/\D/g, '') })}
                            />
                          </label>
                          <label className="cdlg__field">
                            <span>Kaç öde</span>
                            <input
                              inputMode="numeric"
                              value={form.payQty}
                              onChange={(e) => setForm({ ...form, payQty: e.target.value.replace(/\D/g, '') })}
                            />
                          </label>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </fieldset>
              </aside>

              <div className="cdlg__main">
                <div className="cdlg__field">
                  <span className="cdlg__label-row">
                    Hangi ürünlerde
                    <button type="button" className="cdlg__link" onClick={() => setPickProducts(!pickProducts)}>
                      {pickProducts ? 'Kategorilere dön' : 'Tek tek ürün seç'}
                    </button>
                  </span>
                  <div className="cdlg__chips cdlg__scroll" role="group" aria-label={pickProducts ? 'Ürünler' : 'Kategoriler'}>
                    <button
                      type="button"
                      className={form.categoryIds.length === 0 && form.productIds.length === 0 ? 'is-on' : ''}
                      onClick={() => setForm({ ...form, categoryIds: [], productIds: [] })}
                    >
                      {form.categoryIds.length === 0 && form.productIds.length === 0 && <CheckIcon />}
                      Bütün menü
                    </button>
                    {pickProducts
                      ? activeProducts.map((p) => {
                          const on = form.productIds.includes(p.id)
                          return (
                            <button
                              key={p.id}
                              type="button"
                              className={on ? 'is-on' : ''}
                              aria-pressed={on}
                              onClick={() => setForm({ ...form, productIds: toggle(form.productIds, p.id) })}
                            >
                              {on && <CheckIcon />}
                              {p.name}
                            </button>
                          )
                        })
                      : activeCategories.map((cat) => {
                          const on = form.categoryIds.includes(cat.id)
                          return (
                            <button
                              key={cat.id}
                              type="button"
                              className={on ? 'is-on' : ''}
                              aria-pressed={on}
                              onClick={() => setForm({ ...form, categoryIds: toggle(form.categoryIds, cat.id) })}
                            >
                              {on && <CheckIcon />}
                              {cat.name}
                            </button>
                          )
                        })}
                  </div>
                  {(form.categoryIds.length > 0 || form.productIds.length > 0) && (
                    <small>Seçili: {scopeOf(form)}</small>
                  )}
                </div>

                <div className="cdlg__field">
                  <span>Günler</span>
                  <div className="cdlg__chips" role="group" aria-label="Günler">
                    <button
                      type="button"
                      className={form.days.length === 0 ? 'is-on' : ''}
                      onClick={() => setForm({ ...form, days: [] })}
                    >
                      {form.days.length === 0 && <CheckIcon />}
                      Her gün
                    </button>
                    {WEEK.map((w) => {
                      const on = form.days.includes(w.day)
                      return (
                        <button
                          key={w.day}
                          type="button"
                          className={on ? 'is-on' : ''}
                          aria-pressed={on}
                          onClick={() => setForm({ ...form, days: toggle(form.days, w.day) })}
                        >
                          {on && <CheckIcon />}
                          {w.label}
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div className="cdlg__quad">
                  <div className="cdlg__field">
                    <span>Saat başlangıç</span>
                    <TimeField label="Saat başlangıç" value={form.startTime} onChange={(v) => setForm({ ...form, startTime: v })} />
                  </div>
                  <div className="cdlg__field">
                    <span>Saat bitiş</span>
                    <TimeField label="Saat bitiş" value={form.endTime} onChange={(v) => setForm({ ...form, endTime: v })} />
                  </div>
                  <div className="cdlg__field">
                    <span>İlk gün</span>
                    <DateField label="İlk gün" value={form.startsOn} onChange={(v) => setForm({ ...form, startsOn: v })} />
                  </div>
                  <div className="cdlg__field">
                    <span>Son gün</span>
                    <DateField label="Son gün" value={form.endsOn} onChange={(v) => setForm({ ...form, endsOn: v })} />
                  </div>
                </div>
                <small className="cdlg__note">Saat ve tarih boş kalırsa kampanya her zaman geçerli.</small>

                {/* QR menü (16 Eki 2026): müşteri karekodu okutunca kampanya
                    görselli bir pencerede çıkıyor. Afiş yoksa stok fotoğraf. */}
                <div className={`cdlg__qr ${form.showOnQr ? 'is-on' : ''}`}>
                  <div className="cdlg__qr-row">
                    <span className="cdlg__qr-text">
                      <b>QR menüde göster</b>
                      <small>Karekodu okutan müşteriye pencereyle çıkar</small>
                    </span>
                    <button
                      type="button"
                      className={`camp-set__switch ${form.showOnQr ? 'is-on' : ''}`}
                      role="switch"
                      aria-checked={form.showOnQr}
                      aria-label="QR menüde göster"
                      onClick={() => setForm({ ...form, showOnQr: !form.showOnQr })}
                    >
                      <span />
                    </button>
                  </div>
                  {form.showOnQr && (
                    <div className="cdlg__poster">
                      <button
                        type="button"
                        className="cdlg__poster-img"
                        onClick={() => photoOn && fileInput.current?.click()}
                        disabled={!photoOn || uploading}
                        aria-label={form.imageUrl ? 'Afişi değiştir' : 'Afiş ekle'}
                      >
                        <img src={form.imageUrl ?? stockOf(form)} alt="" />
                        {!form.imageUrl && <span className="cdlg__poster-tag">Hazır fotoğraf</span>}
                        {uploading && <span className="cdlg__poster-busy">Yükleniyor…</span>}
                      </button>
                      <div className="cdlg__poster-side">
                        {photoOn ? (
                          <>
                            <small>
                              {form.imageUrl
                                ? 'Kendi afişiniz. Yatay fotoğraf en iyi görünür.'
                                : 'Ürünlere göre hazır fotoğraf seçildi. İsterseniz kendi afişinizi yükleyin.'}
                            </small>
                            <div className="cdlg__poster-btns">
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                disabled={uploading}
                                onClick={() => fileInput.current?.click()}
                              >
                                {form.imageUrl ? 'Değiştir' : 'Afiş yükle'}
                              </button>
                              {form.imageUrl && (
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--sm"
                                  disabled={uploading}
                                  onClick={() => {
                                    if (form.imageUrl !== form.savedImageUrl) void deleteMenuImage(form.imageUrl)
                                    setForm({ ...form, imageUrl: null })
                                  }}
                                >
                                  Kaldır
                                </button>
                              )}
                            </div>
                          </>
                        ) : (
                          <small>Ürünlere göre hazır fotoğraf seçildi. Kendi afişiniz Görselli menü modülüyle açılır.</small>
                        )}
                      </div>
                      <input
                        ref={fileInput}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => void onPickImage(e.target.files?.[0])}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <footer className="cdlg__foot">
              {error ? <p className="cdlg__error">{error}</p> : <span />}
              <div className="cdlg__buttons">
                <button type="button" className="btn btn--ghost" onClick={close}>
                  Vazgeç
                </button>
                <button type="submit" className="btn btn--primary" disabled={busy || !form.name.trim()}>
                  {busy ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
              </div>
            </footer>
          </form>
        </div>
      )}

      {confirmRemove && (
        <div className="dlg" role="dialog" aria-modal="true" aria-label="Kampanyayı sil">
          <div className="dlg__card dlg__card--ask">
            <h2>{confirmRemove.name} silinsin mi?</h2>
            <p className="dlg__empty">
              Kasada artık önerilmez. Daha önce bu kampanyayla kapanan adisyonlar ve raporlar değişmez.
            </p>
            <div className="dlg__buttons dlg__buttons--even">
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmRemove(null)}>
                Vazgeç
              </button>
              <button type="button" className="btn btn--danger" disabled={busy} onClick={() => void onRemove()}>
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
